import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import Masonry from 'react-masonry-css';
import ProductBlock from 'shared/components/likiee/ProductBlock';
import SkeletonLoader from 'shared/components/ui/SkeletonLoader';
import { ArrowLeftLargeIcon } from 'shared/components/Icons';
import { getError } from 'utils/appHelpers';
import { validateUrlParam } from 'utils/securityHelpers';
import { API, graphqlOperation } from 'aws-amplify';
import { getUser, getUserProjectLikiees, getUserProjects } from 'graphql/user/queries';
import UserInfoBlock from '../UserView/components/UserInfoBlock';
import { useLastLocation } from 'react-router-last-location';
import { getLocalizedPath } from 'utils/languageHelpers';
import './UserProjects.scss';

const UserProjects = ({ match, history }) => {
  const { t } = useTranslation();
  const lastLocation = useLastLocation();
  const alert = useAlert();
  const [error, setError] = useState(null);
  const [project, setProject] = useState();
  const [products, setProducts] = useState();
  const [user, setUser] = useState();

  // 🔒 Security: Validate URL parameters before using them
  const validateParams = () => {
    try {
      const projectID = validateUrlParam(match.params?.projectId, 'Project ID', {
        required: true,
        maxLength: 100,
      });
      const userId = validateUrlParam(match.params?.id, 'User ID', {
        required: true,
        maxLength: 100,
      });
      return { projectID, userId, isValid: true };
    } catch (err) {
      setError(err.message);
      console.error('[Security] Invalid URL parameters:', err);
      return { projectID: null, userId: null, isValid: false };
    }
  };
  const goBack = () => {
    // If there's a last location and it's not the auth page, go back
    if (lastLocation && !lastLocation.pathname.includes('/auth')) {
      history.goBack();
    } else {
      // Otherwise go to dashboard
      history.push(getLocalizedPath('/dashboard'));
    }
  };
  const getProjectData = async (userId, projectID) => {
    try {
      const projects = await API.graphql(graphqlOperation(getUserProjects, { userId }));
      const current = projects.data.getUser.projects?.items.find(i => i.id === projectID);
      if (!current) {
        throw new Error('Project not found');
      }
      setProject(current);
      const products = await API.graphql(graphqlOperation(getUserProjectLikiees, { projectId: current.id }));
      setProducts(products.data.getUserProjectLikiees.items);
    } catch (err) {
      console.error('[API Error] getProjectData:', err);
      alert.error(getError(err));
    }
  };
  const getUserData = async id => {
    try {
      const body = { limit: 1000, id };
      const { data } = await API.graphql(graphqlOperation(getUser, body));
      setUser(data.getUser);
    } catch (err) {
      console.error('[API Error] getUserData:', err);
      alert.error(getError(err));
    }
  };
  const onProductClick = product => {
    const { userId, projectID } = validateParams();
    if (userId && projectID) {
      history.push(getLocalizedPath(`/user/${userId}/project/${projectID}/p/${product.id}`));
    }
  };
  useEffect(() => {
    const { projectID, userId, isValid } = validateParams();
    if (!isValid) {
      alert.error(error || 'Invalid URL parameters');
      history.push(getLocalizedPath('/dashboard'));
      return;
    }
    getProjectData(userId, projectID);
    getUserData(userId);
  }, [match.params?.projectId, match.params?.id]);

  return (
    <div className='user-projects-page products-page pb-10'>
      {user && <UserInfoBlock user={user} getUserData={getUserData} className='mb-15' />}
      <div className='user-projects-page__header'>
        <div className='user-projects-page__title-section'>
          <div className='user-projects-page__title-row'>
            <button className='user-projects-page__back-button' onClick={goBack} aria-label='Go back'>
              <ArrowLeftLargeIcon />
            </button>
            <h1 className='user-projects-page__title user-projects-page__title--gradient'>
              {project?.name || 'Loading...'}
            </h1>
          </div>
          <p className='user-projects-page__subtitle d-none d-md-block'>
            {products?.length || 0} {t('text_products')}
          </p>
        </div>
        {products?.length > 0 && (
          <div className='user-projects-page__badge'>
            <span>
              {products.length} {t('text_products')}
            </span>
          </div>
        )}
      </div>
      {!products || !project ? (
        <div className='products-loading-skeleton'>
          <SkeletonLoader type='product-grid' count={8} />
        </div>
      ) : (
        <>
          {!!products?.length && (
            <Masonry
              breakpointCols={{
                default: 4,
                1100: 3,
                500: 2,
              }}
              className='recomended-items-grid pl-6 pl-md-0'
              columnClassName='recomended-items-grid-column'
            >
              {products?.map((item, i) => {
                return (
                  <ProductBlock
                    product={item}
                    key={i}
                    showAlarm={true}
                    showDate={true}
                    notAvailable={item.availability === 'OutOfStock'}
                    onProductClick={onProductClick.bind(null, item)}
                  />
                );
              })}
            </Masonry>
          )}
          {!products?.length && (
            <div className='products-empty-state'>
              <div className='products-empty-state__icon'>📦</div>
              <h2 className='products-empty-state__title'>{t('no_product_in_project')}</h2>
              <p className='products-empty-state__description'>
                Start adding products to your project to see them here
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};
export default UserProjects;
